import { useState, useMemo, useEffect } from 'react';
import { Input, Select, Table, Tag, DatePicker, Spin, Grid, Button, Tooltip, message, Popconfirm, notification } from 'antd';
import { useGetAuditLogs } from './hooks';
import { useGenerateCustomReport } from '../report/Hook/Index';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { SearchOutlined, DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { unixToLocalDate } from '../../utils/CommonFunctions';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useNavigate } from 'react-router-dom';
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const AuditLogs = () => {
  const { t } = useTranslation(['audit', 'common']);
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [action, setAction] = useState(undefined);
  const [category, setCategory] = useState(undefined);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { operatorId } = useOperatorStore();
  // Default date range: Past 30 days
  const defaultStartDate = dayjs().subtract(30, 'days');
  const defaultEndDate = dayjs();
  const [dateRange, setDateRange] = useState([defaultStartDate, defaultEndDate]);
  const { mutate: generateReport, isPending: isExporting } = useGenerateCustomReport();

  const datePresets = [
    { label: tCommon('today'), value: [dayjs(), dayjs()] },
    { label: tCommon('yesterday'), value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')] },
    { label: tCommon('last7Days'), value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: tCommon('last30Days'), value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: tCommon('thisMonth'), value: [dayjs().startOf('month'), dayjs().endOf('month')] },
    { label: tCommon('lastMonth'), value: [dayjs().add(-1, 'month').startOf('month'), dayjs().add(-1, 'month').endOf('month')] },
  ];

  // Action Types from API Enum
  const actionTypes = [
    'CREATE',
    'READ',
    'UPDATE',
    'DELETE',
    'LOGIN',
    'OTHER',
    'OCPP_CONFIG',
  ];

  // Categories from API Enum
  const categories = [
    'OPERATOR',
    'RFID',
    'CHARGER',
    'LOCATION',
    'LOCATION_OWNER',
    'CUSTOMER',
    'CHARGING_HISTORY',
    'REPORT',
    'OCPP_LOGS',
    'SUPER_ADMIN',
    'ADMIN',
    'PASSWORD',
    'OCPP_CONFIG',
  ];

  // Construct API query parameters
  const params = useMemo(
    () => ({
      performedBy: undefined,
      actionType: action || undefined,
      category: category || undefined,
      operatorId: operatorId || undefined,
      search: search || undefined,
      startDate:
        dateRange?.[0]?.format('YYYY-MM-DD') ||
        defaultStartDate.format('YYYY-MM-DD'),
      endDate:
        dateRange?.[1]?.format('YYYY-MM-DD') ||
        defaultEndDate.format('YYYY-MM-DD'),
      offset: page,
      limit: pageSize,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [action, category, search, dateRange, page, pageSize]
  );

  // Fetch audit logs from API
  const { data, isLoading } = useGetAuditLogs({ params });

  // Extract data from API response
  const logs = data?.paginatedData || [];
  const totalCount = data?.totalCount || 0;

  // Reset pagination when filters change
  useEffect(() => {
    setPage(1);
  }, [action, category, search, dateRange]);

  // Function to determine tag color
  const getActionColor = (action) => {
    const colorMap = {
      CREATE: 'green',
      READ: 'blue',
      UPDATE: 'orange',
      DELETE: 'red',
      LOGIN: 'cyan',
      LOGOUT: 'purple',
    };
    return colorMap[action] || 'gray';
  };

  // Function to check if date range is more than 3 months
  const isDateRangeMoreThan3Months = (startDate, endDate) => {
    if (!startDate || !endDate) return false;
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const monthsDiff = end.diff(start, 'month', true);
    return monthsDiff > 3;
  };

  // Function to proceed with report generation
  const proceedWithReportGeneration = () => {
    const payload = {
      reportTemplate: "AUDIT_REPORT",
      operatorId: operatorId,
      startTime: dateRange[0].format('YYYY-MM-DD'),
      endTime: dateRange[1].format('YYYY-MM-DD'),
      reportName: `Audit_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}`,
    };

    generateReport(payload, {
      onSuccess: () => {
        notification.success({
          message: t('reportGenerationStarted') || 'Report generation started successfully',
          description: t('reportAvailableInCustomReports') || 'Your report will be available in Custom Reports section',
          duration: 6,
          btn: (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                navigate('/dashboard/reports', {
                  state: { activeMainTab: 'customReports' },
                });
                notification.destroy();
              }}
            >
              {t('viewCustomReports') || 'View Custom Reports'}
            </Button>
          ),
        });
      },
      onError: (error) => {
        console.error('Export failed:', error);
        // Extract backend error message
        const errorMessage = 
          error?.response?.data?.message ||
          error?.response?.data?.status_message ||
          error?.response?.data?.error ||
          error?.message ||
          t('reportGenerationFailed') || 'Report generation failed';
        message.error(errorMessage);
      }
    });
  };

  // Check if we need to show Popconfirm (date range > 3 months)
  const shouldShowPopconfirm = useMemo(() => {
    const hasDateRange = dateRange && dateRange.length === 2 && operatorId;
    
    if (!hasDateRange) return false;
    
    return isDateRangeMoreThan3Months(dateRange[0], dateRange[1]);
  }, [dateRange, operatorId]);

  // Function to handle export logic (without confirmation)
  const executeExport = () => {
    if (!logs.length) {
      message.warning(t('noDataToExport') || 'No data to export');
      return;
    }

    // Check if date range is selected
    if (dateRange && dateRange.length === 2) {
      // Generate custom report when date filters are applied
      proceedWithReportGeneration();
    } else {
      // Fallback to client-side CSV export (using PapaParse if available, or simple CSV generation)
      // Since PapaParse wasn't imported in this file, I'll use a simple CSV generation or import it if I can.
      // For now, I'll assume we can just generate a simple CSV string.
      try {
        const headers = ['Timestamp', 'User', 'Action', 'Category', 'Details'];
        const csvRows = [headers.join(',')];

        logs.forEach(log => {
          const row = [
            unixToLocalDate(log.createdAt),
            log.combinedUserDetails ? `${log.combinedUserDetails.fname} ${log.combinedUserDetails.lname} (${log.combinedUserDetails.email})` : 'N/A',
            log.actionType,
            log.category,
            `"${(log.description || '').replace(/"/g, '""')}"` // Escape quotes
          ];
          csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success(tCommon('exportSuccessCount', { count: logs.length }));
      } catch (error) {
        console.error('Export failed:', error);
        message.error(tCommon('exportFailed'));
      }
    }
  };

  // Export button component
  const ExportButton = () => {
    const isDisabled = !logs.length;
    
    const buttonProps = {
      icon: <DownloadOutlined />,
      type: 'default',
      loading: isExporting,
      disabled: isDisabled,
    };

    if (shouldShowPopconfirm && !isDisabled) {
      return (
        <Popconfirm
          title={t('longPeriodWarningTitle') || 'Long Period Selected'}
          description={t('longPeriodWarningMessage') || 'The selected date range is more than 3 months. Report generation may take some time. Do you want to continue?'}
          okText={t('ok') || 'OK'}
          cancelText={t('cancel') || 'Cancel'}
          onConfirm={executeExport}
          icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
        >
          <Button {...buttonProps}>
            {!isMobile && t('export')}
          </Button>
        </Popconfirm>
      );
    }

    return (
      <Button
        {...buttonProps}
        onClick={executeExport}
      >
        {!isMobile && t('export')}
      </Button>
    );
  };

  // Table Columns
  const columns = useMemo(
    () => [
      {
        title: t('timestamp'), // Use translation
        dataIndex: 'createdAt',
        key: 'createdAt',
        align: 'center',
        width: isMobile ? 120 : 150,
        sorter: (a, b) => a.createdAt - b.createdAt,
        render: (timestamp) => (
          <div className={`${isMobile ? 'text-xs' : 'text-sm'}`}>
            {unixToLocalDate(timestamp)}
          </div>
        ),
      },
      {
        title: t('user'), // Use translation
        dataIndex: 'combinedUserDetails',
        key: 'combinedUserDetails',
        align: 'center',
        width: isMobile ? 140 : 200,
        render: (user) => (
          <div className={`${isMobile ? 'text-xs' : 'text-sm'}`}>
            {user
              ? `${user.fname} ${user.lname} (${user.email})`
              : t('notAvailable')} {/* Use translation */}
          </div>
        ),
      },
      {
        title: t('action'), // Use translation
        dataIndex: 'actionType',
        key: 'actionType',
        align: 'center',
        width: isMobile ? 80 : 100,
        render: (action) => (
          <Tag color={getActionColor(action)} className={`${isMobile ? 'text-xs' : 'text-sm'}`} variant="outlined">
            {t(action)}
          </Tag>
        ), // Use translation
      },
      {
        title: t('category'), // Use translation
        dataIndex: 'category',
        key: 'category',
        align: 'center',
        width: isMobile ? 100 : 120,
        render: (category) => (
          <Tag color="blue" className={`${isMobile ? 'text-xs' : 'text-sm'}`} variant="outlined">
            {t(category)}
          </Tag>
        ), // Use translation
      },
      {
        title: t('details'),
        dataIndex: 'description',
        key: 'description',
        align: 'center',
        width: isMobile ? 150 : 250,
        render: (text) => (
          <div className={`${isMobile ? 'text-xs' : 'text-sm'} max-w-[200px] lg:max-w-none`}>
            {text}
          </div>
        ),
      }, // Use translation
    ],
    [t, isMobile] // Add t and isMobile as dependencies
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row items-start gap-4 justify-start">
        <Input
          allowClear
          className={`${isMobile ? 'w-full' : 'w-full'}`}
          prefix={<SearchOutlined />}
          placeholder={t('searchLogsByDescription')} // Use translation
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          allowClear
          className={`${isMobile ? 'w-full' : 'w-full'}`}
          options={actionTypes.map((a) => ({ label: t(a), value: a }))} // Use translation
          placeholder={t('selectActionType')} // Use translation
          value={action}
          onChange={setAction}
        />
        <Select
          allowClear
          className={`${isMobile ? 'w-full' : 'w-full'}`}
          options={categories.map((c) => ({ label: t(c), value: c }))} // Use translation
          placeholder={t('selectCategory')} // Use translation
          value={category}
          onChange={setCategory}
        />
        <RangePicker
          className={`${isMobile ? 'w-full' : 'w-full'}`}
          value={dateRange}
          onChange={setDateRange}
          presets={datePresets}
          disabledDate={(current) => {
            return (
              current &&
              (current.isBefore(defaultStartDate) ||
                current.isAfter(defaultEndDate))
            );
          }}
        />
        <Tooltip title={t('exportCSV')}>
          <ExportButton />
        </Tooltip>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spin size="small" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={logs}
            pagination={{
              current: page,
              pageSize: isMobile ? 5 : pageSize,
              total: totalCount,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
              size: isMobile ? "small" : "default",
              showSizeChanger: false,
              position: ['bottomCenter'],
            }}
            variant="outlined"
            rowKey="_id"
            showSorterTooltip={false}
            scroll={{ x: isMobile ? 800 : 'max-content', y: isMobile ? 60 * 5 : 60 * 8 }}
          />
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
